'use client';

import React, { useState, useEffect } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────
interface NavItem {
  label: string;
  href: string;
}

interface Service {
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface Feature {
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface TeamMember {
  name: string;
  role: string;
  description: string;
}

interface Testimonial {
  quote: string;
  author: string;
  practice: string;
}

interface ContactInfo {
  address: string;
  phone: string;
  email: string;
  hours: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────
const navItems: NavItem[] = [
{ label: 'Home', href: '#home' },
{ label: 'About Us', href: '#about' },
{ label: 'Services', href: '#services' },
{ label: 'Why Choose Us', href: '#why-us' },
{ label: 'Contact Us', href: '#contact' }];


const services: Service[] = [
{
  title: 'Porcelain Crowns & Bridges',
  description:
  'Precision-crafted porcelain-fused-to-metal and all-ceramic crowns and bridges that deliver exceptional aesthetics and long-lasting durability. Each restoration is custom-shaded to match your patient\'s natural dentition.',
  icon:
  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>

},
{
  title: 'Complete & Partial Dentures',
  description:
  'Custom-fabricated complete and partial dentures designed for optimal fit, function, and natural appearance. We use premium acrylic resins and high-quality teeth to ensure patient comfort and satisfaction.',
  icon:
  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>

},
{
  title: 'Dental Implant Restorations',
  description:
  'State-of-the-art implant crowns, abutments, and implant-supported prosthetics. We work with all major implant systems and provide precise, digitally-designed restorations for predictable outcomes.',
  icon:
  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>

},
{
  title: 'Zirconia Restorations',
  description:
  'High-strength, translucent zirconia crowns and bridges milled with CAD/CAM precision. Our zirconia restorations offer superior strength, biocompatibility, and outstanding esthetics for anterior and posterior cases.',
  icon:
  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>

},
{
  title: 'Orthodontic Appliances',
  description:
  'Custom orthodontic retainers, Hawley appliances, space maintainers, and functional appliances. Fabricated with precision to meet your exact specifications for optimal patient outcomes.',
  icon:
  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4a2 2 0 101.806-1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
      </svg>

},
{
  title: 'Night Guards & Occlusal Splints',
  description:
  'Custom-fabricated occlusal splints and night guards for bruxism management and TMJ therapy. Precision-fitted for maximum patient comfort and therapeutic effectiveness.',
  icon:
  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
      </svg>

}];


const features: Feature[] = [
{
  title: 'Highest Quality Products',
  description:
  'We are FDA certified and provide a 100% satisfaction guarantee with every product and service. Our technicians bring decades of expertise to every restoration.',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>

},
{
  title: 'Competitive Pricing',
  description:
  'Call us and you will see our competitive pricing that will allow you to significantly lower your lab bill without compromising on quality or turnaround time.',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>

},
{
  title: 'Fast Turnaround & Delivery',
  description:
  'We deliver patient products in record time. Personal delivery to all local areas at no cost, ensuring your practice runs smoothly and patients are never kept waiting.',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>

},
{
  title: 'Digital Integration',
  description:
  'CEREC, Medit, iTero & Carestream partners. We receive any STL digital intraoral scanner files, streamlining your digital workflow for faster, more accurate restorations.',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>

},
{
  title: 'FDA Certified Laboratory',
  description:
  'Our laboratory is FDA certified, ensuring all products meet the highest regulatory standards for safety and quality. Your patients deserve nothing less than the best.',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>

},
{
  title: '40+ Years of Expertise',
  description:
  'With over 40 years of knowledge and expertise in restorative dentistry, our experienced team brings unmatched skill and craftsmanship to every case we handle.',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>

}];


const testimonials: Testimonial[] = [
{
  quote:
  'Dental Quest Laboratory has been our trusted lab partner for over 15 years. Their quality is consistently outstanding, turnaround times are always met, and the team is incredibly responsive. I would not trust my patients\' restorations to anyone else.',
  author: 'Dr. Michael Chen',
  practice: 'Valley Dental Associates, Rockland County, NY'
},
{
  quote:
  'The zirconia crowns and bridges from Dental Quest are exceptional. The fit is always precise, the esthetics are beautiful, and the pricing is very competitive. Their digital workflow integration has transformed how we handle complex cases.',
  author: 'Dr. Sarah Patel',
  practice: 'Patel Family Dentistry, Nanuet, NY'
},
{
  quote:
  'I switched to Dental Quest Laboratory two years ago and have never looked back. Their FDA certification, attention to detail, and personal delivery service make them the ideal lab partner for any dental practice in the Hudson Valley area.',
  author: 'Dr. James Rodriguez',
  practice: 'Smile Center of Westchester, White Plains, NY'
}];


const contactInfo: ContactInfo = {
  address: '495 Kings Hwy, Valley Cottage, NY 10989',
  phone: '(843) 251-8992',
  email: 'dentalquestsupport@dentaquelab.com',
  hours: 'Mon–Fri: 8:00 AM – 5:00 PM'
};

// ─── Scroll Hook ──────────────────────────────────────────────────────────────
function useScrollAnimation() {
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return visibleSections;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [scrolled, setScrolled] = useState(false);
  const visibleSections = useScrollAnimation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);

      const sections = ['home', 'about', 'services', 'why-us', 'contact'];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('sending');
    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setFormStatus('sent');
    setFormData({ fullName: '', email: '', phone: '', message: '' });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9f9f9', fontFamily: 'Raleway, sans-serif' }}>
      {/* ─── Navigation ─────────────────────────────────────────────────── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-300 ${
        scrolled ? 'shadow-md' : ''}`
        }
        style={{ backgroundColor: '#ffffff' }}>
        
        {/* Brand Title */}
        <div className="text-center py-4 px-4">
          <button
            onClick={() => handleNavClick('#home')}
            className="inline-block transition-opacity duration-200 hover:opacity-80">
            
            <span
              className="font-raleway font-normal"
              style={{
                color: '#5040ae',
                fontSize: '36px',
                letterSpacing: '3px',
                lineHeight: 1.2
              }}>
              
              Dental Quest Laboratory, Inc.
            </span>
          </button>
        </div>

        {/* Desktop Nav */}
        <nav
          className="hidden md:flex items-center justify-center border-t border-gray-100"
          style={{ backgroundColor: '#ffffff' }}>
          
          <div className="flex items-center">
            {navItems.map((item) =>
            <button
              key={item.href}
              onClick={() => handleNavClick(item.href)}
              className={`px-5 py-3 text-xs font-medium transition-colors duration-200 font-montserrat ${
              activeSection === item.href.replace('#', '') ?
              'border-b-2 border-brand-teal' : 'hover:text-brand-teal'}`
              }
              style={{
                color: activeSection === item.href.replace('#', '') ? '#5199a8' : '#484848',
                letterSpacing: '1px',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '11px',
                fontWeight: 400
              }}>
              
                {item.label}
              </button>
            )}
          </div>
        </nav>

        {/* Mobile Nav Toggle */}
        <div className="md:hidden flex items-center justify-end px-4 pb-3">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded"
            style={{ color: '#484848' }}
            aria-label="Toggle menu">
            
            <div className="w-6 h-0.5 bg-current mb-1.5 transition-all duration-200" />
            <div className="w-6 h-0.5 bg-current mb-1.5 transition-all duration-200" />
            <div className="w-6 h-0.5 bg-current transition-all duration-200" />
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen &&
        <div
          className="md:hidden border-t border-gray-100"
          style={{ backgroundColor: '#ffffff' }}>
          
            {navItems.map((item) =>
          <button
            key={item.href}
            onClick={() => handleNavClick(item.href)}
            className="block w-full text-left px-6 py-3 text-xs border-b border-gray-50 hover:bg-gray-50 transition-colors duration-150"
            style={{
              color: '#484848',
              letterSpacing: '1px',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '11px'
            }}>
            
                {item.label}
              </button>
          )}
          </div>
        }
      </header>

      {/* ─── Hero Section ───────────────────────────────────────────────── */}
      <section
        id="home"
        className="relative w-full overflow-hidden"
        style={{ paddingTop: '140px' }}>
        
        <div
          className="relative w-full"
          style={{ height: '380px' }}>
          
          {/* Hero background */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
              "https://images.unsplash.com/photo-1619534395675-8a62d8ec5188",
              backgroundPosition: '50% 50%'
            }} />
          
          {/* Overlay */}
          <div
            className="absolute inset-0"
            style={{ backgroundColor: 'rgba(80, 64, 174, 0.45)' }} />
          
          {/* Hero Content */}
          <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
            <h1
              className="font-playfair font-normal text-white mb-4"
              style={{
                fontFamily: '"Playfair Display", serif',
                fontSize: 'clamp(32px, 5vw, 72px)',
                fontWeight: 400,
                lineHeight: 1.2,
                textShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}>
              
              Your Reliable Dental Lab Partner
            </h1>
            <p
              className="text-white mb-8 max-w-2xl"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: 'clamp(13px, 2vw, 16px)',
                letterSpacing: '1px',
                lineHeight: '1.6',
                textShadow: '0 1px 4px rgba(0,0,0,0.3)'
              }}>
              
              Full-service dental laboratory in Valley Cottage, NY — over 40 years of expertise in restorative dentistry
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => handleNavClick('#services')}
                className="transition-colors duration-200"
                style={{
                  backgroundColor: '#5199a8',
                  color: '#ffffff',
                  padding: '12px 32px',
                  fontFamily: 'Raleway, sans-serif',
                  letterSpacing: '2px',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '0px'
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#5cb0c2';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#5199a8';
                }}>
                
                Our Services
              </button>
              <button
                onClick={() => handleNavClick('#contact')}
                className="transition-colors duration-200"
                style={{
                  backgroundColor: 'transparent',
                  color: '#ffffff',
                  padding: '12px 32px',
                  fontFamily: 'Raleway, sans-serif',
                  letterSpacing: '2px',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  border: '2px solid #ffffff',
                  cursor: 'pointer',
                  borderRadius: '0px'
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255,255,255,0.15)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                }}>
                
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Intro / Stats Bar ──────────────────────────────────────────── */}
      <section
        className="py-8"
        style={{ backgroundColor: '#5199a8' }}>
        
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
            { value: '40+', label: 'Years of Experience' },
            { value: 'FDA', label: 'Certified Laboratory' },
            { value: '100%', label: 'Satisfaction Guarantee' },
            { value: 'FREE', label: 'Local Delivery' }].
            map((stat) =>
            <div key={stat.label} className="text-white">
                <div
                className="font-raleway font-bold"
                style={{ fontSize: '28px', letterSpacing: '1px' }}>
                
                  {stat.value}
                </div>
                <div
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '10px',
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  opacity: 0.9,
                  marginTop: '4px'
                }}>
                
                  {stat.label}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── About Section ──────────────────────────────────────────────── */}
      <section
        id="about"
        data-animate
        className={`py-20 transition-all duration-700 ${
        visibleSections.has('about') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
        }
        style={{ backgroundColor: '#ffffff' }}>
        
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Text */}
            <div>
              <p
                className="uppercase mb-3"
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '11px',
                  letterSpacing: '2px',
                  color: '#5199a8',
                  fontWeight: 600
                }}>
                
                About Us
              </p>
              <h2
                className="mb-6"
                style={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: '37px',
                  fontWeight: 400,
                  color: '#42514c',
                  lineHeight: 1.3
                }}>
                
                Crafting Excellence in Dental Restorations Since 1984
              </h2>
              <div
                style={{
                  fontFamily: '"Gentium Basic", serif',
                  fontSize: '18px',
                  lineHeight: '34px',
                  color: '#7b8c89'
                }}>
                
                <p className="mb-4">
                  Dental Quest Laboratory, Inc. is a full-service dental laboratory located at 495 Kings Hwy, Valley Cottage, NY 10989. With over 40 years of knowledge and expertise in restorative dentistry, we have built a reputation for delivering the highest quality dental prosthetics and restorations.
                </p>
                <p className="mb-4">
                  Our mission is simple: provide dental professionals with the finest laboratory services at competitive prices, with fast turnaround times and exceptional customer support. We are proud to be an FDA certified laboratory, ensuring every product we deliver meets the strictest quality and safety standards.
                </p>
                <p>
                  From single-unit crowns to complex full-arch restorations, our skilled technicians bring artistry and precision to every case. We are committed to being your most reliable dental lab partner.
                </p>
              </div>
              <button
                onClick={() => handleNavClick('#contact')}
                className="mt-8 transition-colors duration-200"
                style={{
                  backgroundColor: '#5199a8',
                  color: '#ffffff',
                  padding: '10px 28px',
                  fontFamily: 'Raleway, sans-serif',
                  letterSpacing: '2px',
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '0px',
                  display: 'inline-block'
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#5cb0c2';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#5199a8';
                }}>
                
                Schedule a Free Consultation
              </button>
            </div>

            {/* Image Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 overflow-hidden" style={{ height: '220px' }}>
                <img
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_194948617-1765215112824.png"
                  alt="Dental laboratory technician working on dental prosthetics"
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                
              </div>
              <div className="overflow-hidden" style={{ height: '160px' }}>
                <img
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1ff63d7e4-1775148274606.png"
                  alt="Precision dental crown fabrication in laboratory"
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                
              </div>
              <div className="overflow-hidden" style={{ height: '160px' }}>
                <img
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1f0636b2b-1772826984582.png"
                  alt="Modern dental laboratory equipment and tools"
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Services Section ────────────────────────────────────────────── */}
      <section
        id="services"
        data-animate
        className={`py-20 transition-all duration-700 ${
        visibleSections.has('services') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
        }
        style={{ backgroundColor: '#f9f9f9' }}>
        
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p
              className="uppercase mb-3"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '11px',
                letterSpacing: '2px',
                color: '#5199a8',
                fontWeight: 600
              }}>
              
              Our Services
            </p>
            <h2
              style={{
                fontFamily: '"Playfair Display", serif',
                fontSize: '37px',
                fontWeight: 400,
                color: '#42514c',
                lineHeight: 1.3
              }}>
              
              Comprehensive Dental Laboratory Services
            </h2>
            <p
              className="mt-4 max-w-2xl mx-auto"
              style={{
                fontFamily: '"Gentium Basic", serif',
                fontSize: '18px',
                lineHeight: '34px',
                color: '#7b8c89'
              }}>
              
              From traditional prosthetics to cutting-edge digital restorations, we offer a complete range of dental laboratory services tailored to meet the needs of dental professionals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) =>
            <div
              key={service.title}
              className="group p-6 bg-white transition-all duration-300 hover:shadow-lg"
              style={{
                borderTop: '3px solid transparent',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.borderTopColor = '#5199a8';
                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.borderTopColor = 'transparent';
                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
              }}>
              
                <div
                className="mb-4"
                style={{ color: '#5199a8' }}>
                
                  {service.icon}
                </div>
                <h3
                className="mb-3"
                style={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: '17px',
                  fontWeight: 600,
                  color: '#42514c',
                  lineHeight: 1.4
                }}>
                
                  {service.title}
                </h3>
                <p
                style={{
                  fontFamily: '"Gentium Basic", serif',
                  fontSize: '15px',
                  lineHeight: '26px',
                  color: '#7b8c89'
                }}>
                
                  {service.description}
                </p>
              </div>
            )}
          </div>

          {/* CTA Banner */}
          <div
            className="mt-14 p-8 text-center"
            style={{ backgroundColor: '#42514c' }}>
            
            <h3
              className="text-white mb-3"
              style={{
                fontFamily: '"Playfair Display", serif',
                fontSize: '26px',
                fontWeight: 400
              }}>
              
              Ready to Partner with Dental Quest Laboratory?
            </h3>
            <p
              className="text-white mb-6 opacity-80"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '13px',
                letterSpacing: '0.5px'
              }}>
              
              Contact us today at {contactInfo.phone} to schedule a free business consultation.
            </p>
            <button
              onClick={() => handleNavClick('#contact')}
              className="transition-colors duration-200"
              style={{
                backgroundColor: '#5199a8',
                color: '#ffffff',
                padding: '10px 28px',
                fontFamily: 'Raleway, sans-serif',
                letterSpacing: '2px',
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '0px'
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#5cb0c2';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#5199a8';
              }}>
              
              Get in Touch
            </button>
          </div>
        </div>
      </section>

      {/* ─── Why Choose Us ──────────────────────────────────────────────── */}
      <section
        id="why-us"
        data-animate
        className={`py-20 transition-all duration-700 ${
        visibleSections.has('why-us') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
        }
        style={{ backgroundColor: '#ffffff' }}>
        
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p
              className="uppercase mb-3"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '11px',
                letterSpacing: '2px',
                color: '#5199a8',
                fontWeight: 600
              }}>
              
              Why Choose Us
            </p>
            <h2
              style={{
                fontFamily: '"Playfair Display", serif',
                fontSize: '37px',
                fontWeight: 400,
                color: '#42514c',
                lineHeight: 1.3
              }}>
              
              The Dental Quest Difference
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) =>
            <div
              key={feature.title}
              className="flex gap-4 p-6 transition-all duration-300 hover:bg-gray-50">
              
                <div
                className="flex-shrink-0 w-14 h-14 flex items-center justify-center"
                style={{ backgroundColor: 'rgba(81, 153, 168, 0.1)', color: '#5199a8' }}>
                
                  {feature.icon}
                </div>
                <div>
                  <h3
                  className="mb-2"
                  style={{
                    fontFamily: '"Playfair Display", serif',
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#42514c'
                  }}>
                  
                    {feature.title}
                  </h3>
                  <p
                  style={{
                    fontFamily: '"Gentium Basic", serif',
                    fontSize: '15px',
                    lineHeight: '26px',
                    color: '#7b8c89'
                  }}>
                  
                    {feature.description}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── Testimonials ───────────────────────────────────────────────── */}
      <section
        data-animate
        id="testimonials"
        className={`py-20 transition-all duration-700 ${
        visibleSections.has('testimonials') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
        }
        style={{ backgroundColor: '#f9f9f9' }}>
        
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p
              className="uppercase mb-3"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '11px',
                letterSpacing: '2px',
                color: '#5199a8',
                fontWeight: 600
              }}>
              
              Testimonials
            </p>
            <h2
              style={{
                fontFamily: '"Playfair Display", serif',
                fontSize: '37px',
                fontWeight: 400,
                color: '#42514c',
                lineHeight: 1.3
              }}>
              
              What Our Clients Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) =>
            <div
              key={testimonial.author}
              className="bg-white p-8 transition-all duration-300 hover:shadow-md"
              style={{ borderLeft: '4px solid #5199a8' }}>
              
                <div
                className="mb-4"
                style={{ color: '#5199a8', fontSize: '32px', lineHeight: 1 }}>
                
                  &ldquo;
                </div>
                <p
                className="mb-6"
                style={{
                  fontFamily: '"Gentium Basic", serif',
                  fontSize: '16px',
                  lineHeight: '28px',
                  color: '#7b8c89',
                  fontStyle: 'italic'
                }}>
                
                  {testimonial.quote}
                </p>
                <div>
                  <div
                  style={{
                    fontFamily: '"Playfair Display", serif',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#42514c'
                  }}>
                  
                    {testimonial.author}
                  </div>
                  <div
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '11px',
                    letterSpacing: '0.5px',
                    color: '#5199a8',
                    marginTop: '4px'
                  }}>
                  
                    {testimonial.practice}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── Contact Section ────────────────────────────────────────────── */}
      <section
        id="contact"
        data-animate
        className={`py-20 transition-all duration-700 ${
        visibleSections.has('contact') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
        }
        style={{ backgroundColor: '#ffffff' }}>
        
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p
              className="uppercase mb-3"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '11px',
                letterSpacing: '2px',
                color: '#5199a8',
                fontWeight: 600
              }}>
              
              Contact Us
            </p>
            <h2
              style={{
                fontFamily: '"Playfair Display", serif',
                fontSize: '37px',
                fontWeight: 400,
                color: '#42514c',
                lineHeight: 1.3
              }}>
              
              Get in Touch With Us
            </h2>
            <p
              className="mt-4 max-w-xl mx-auto"
              style={{
                fontFamily: '"Gentium Basic", serif',
                fontSize: '18px',
                lineHeight: '34px',
                color: '#7b8c89'
              }}>
              
              We look forward to doing business with you. Contact us today to schedule a free business consultation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h3
                className="mb-8"
                style={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: '24px',
                  fontWeight: 400,
                  color: '#42514c'
                }}>
                
                Business Information
              </h3>

              <div className="space-y-6">
                {/* Address */}
                <div className="flex gap-4">
                  <div
                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(81, 153, 168, 0.1)', color: '#5199a8' }}>
                    
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '10px',
                        letterSpacing: '1.5px',
                        textTransform: 'uppercase',
                        color: '#5199a8',
                        marginBottom: '4px'
                      }}>
                      
                      Address
                    </div>
                    <div
                      style={{
                        fontFamily: '"Gentium Basic", serif',
                        fontSize: '17px',
                        lineHeight: '28px',
                        color: '#42514c'
                      }}>
                      
                      {contactInfo.address}
                    </div>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex gap-4">
                  <div
                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(81, 153, 168, 0.1)', color: '#5199a8' }}>
                    
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '10px',
                        letterSpacing: '1.5px',
                        textTransform: 'uppercase',
                        color: '#5199a8',
                        marginBottom: '4px'
                      }}>
                      
                      Phone
                    </div>
                    <a
                      href={`tel:${contactInfo.phone}`}
                      className="transition-colors duration-200 hover:opacity-70"
                      style={{
                        fontFamily: '"Gentium Basic", serif',
                        fontSize: '17px',
                        lineHeight: '28px',
                        color: '#42514c',
                        textDecoration: 'none'
                      }}>
                      
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex gap-4">
                  <div
                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(81, 153, 168, 0.1)', color: '#5199a8' }}>
                    
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '10px',
                        letterSpacing: '1.5px',
                        textTransform: 'uppercase',
                        color: '#5199a8',
                        marginBottom: '4px'
                      }}>
                      
                      Email
                    </div>
                    <a
                      href={`mailto:${contactInfo.email}`}
                      className="transition-colors duration-200 hover:opacity-70"
                      style={{
                        fontFamily: '"Gentium Basic", serif',
                        fontSize: '16px',
                        lineHeight: '28px',
                        color: '#42514c',
                        textDecoration: 'none',
                        wordBreak: 'break-all'
                      }}>
                      
                      {contactInfo.email}
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex gap-4">
                  <div
                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(81, 153, 168, 0.1)', color: '#5199a8' }}>
                    
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '10px',
                        letterSpacing: '1.5px',
                        textTransform: 'uppercase',
                        color: '#5199a8',
                        marginBottom: '4px'
                      }}>
                      
                      Business Hours
                    </div>
                    <div
                      style={{
                        fontFamily: '"Gentium Basic", serif',
                        fontSize: '17px',
                        lineHeight: '28px',
                        color: '#42514c'
                      }}>
                      
                      {contactInfo.hours}
                    </div>
                  </div>
                </div>
              </div>

              {/* Map placeholder */}
              <div
                className="mt-8 overflow-hidden"
                style={{ height: '200px', backgroundColor: '#e8f0ef' }}>
                
                <iframe
                  title="Dental Quest Laboratory Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3010.5!2d-73.9700!3d41.1200!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s495+Kings+Hwy%2C+Valley+Cottage%2C+NY+10989!5e0!3m2!1sen!2sus!4v1"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade" />
                
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h3
                className="mb-8"
                style={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: '24px',
                  fontWeight: 400,
                  color: '#42514c'
                }}>
                
                Send Us a Message
              </h3>

              {formStatus === 'sent' ?
              <div
                className="p-8 text-center"
                style={{ backgroundColor: 'rgba(81, 153, 168, 0.1)', border: '1px solid #5199a8' }}>
                
                  <svg
                  className="w-12 h-12 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ color: '#5199a8' }}>
                  
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h4
                  style={{
                    fontFamily: '"Playfair Display", serif',
                    fontSize: '22px',
                    color: '#42514c',
                    marginBottom: '8px'
                  }}>
                  
                    Message Sent!
                  </h4>
                  <p
                  style={{
                    fontFamily: '"Gentium Basic", serif',
                    fontSize: '16px',
                    color: '#7b8c89',
                    lineHeight: '28px'
                  }}>
                  
                    Thank you for contacting Dental Quest Laboratory. We will get back to you within one business day.
                  </p>
                  <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-6 transition-colors duration-200"
                  style={{
                    backgroundColor: '#5199a8',
                    color: '#ffffff',
                    padding: '8px 24px',
                    fontFamily: 'Raleway, sans-serif',
                    letterSpacing: '2px',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    border: 'none',
                    cursor: 'pointer'
                  }}>
                  
                    Send Another
                  </button>
                </div> :

              <form
                onSubmit={handleFormSubmit}
                action={`mailto:${contactInfo.email}`}
                method="post"
                encType="text/plain">
                
                  {/* Full Name */}
                  <div className="mb-5">
                    <label
                    htmlFor="fullName"
                    style={{
                      display: 'block',
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '10px',
                      letterSpacing: '1.5px',
                      textTransform: 'uppercase',
                      color: '#42514c',
                      marginBottom: '8px',
                      fontWeight: 600
                    }}>
                    
                      Full Name <span style={{ color: '#5199a8' }}>*</span>
                    </label>
                    <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    required
                    value={formData.fullName}
                    onChange={handleFormChange}
                    placeholder="Dr. John Smith"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '1px solid #dbdbdb',
                      borderRadius: '0px',
                      fontFamily: '"Gentium Basic", serif',
                      fontSize: '16px',
                      color: '#42514c',
                      backgroundColor: '#ffffff',
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => {
                      (e.currentTarget as HTMLInputElement).style.borderColor = '#5199a8';
                    }}
                    onBlur={(e) => {
                      (e.currentTarget as HTMLInputElement).style.borderColor = '#dbdbdb';
                    }} />
                  
                  </div>

                  {/* Email */}
                  <div className="mb-5">
                    <label
                    htmlFor="email"
                    style={{
                      display: 'block',
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '10px',
                      letterSpacing: '1.5px',
                      textTransform: 'uppercase',
                      color: '#42514c',
                      marginBottom: '8px',
                      fontWeight: 600
                    }}>
                    
                      Email Address <span style={{ color: '#5199a8' }}>*</span>
                    </label>
                    <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleFormChange}
                    placeholder="doctor@practice.com"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '1px solid #dbdbdb',
                      borderRadius: '0px',
                      fontFamily: '"Gentium Basic", serif',
                      fontSize: '16px',
                      color: '#42514c',
                      backgroundColor: '#ffffff',
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => {
                      (e.currentTarget as HTMLInputElement).style.borderColor = '#5199a8';
                    }}
                    onBlur={(e) => {
                      (e.currentTarget as HTMLInputElement).style.borderColor = '#dbdbdb';
                    }} />
                  
                  </div>

                  {/* Phone */}
                  <div className="mb-5">
                    <label
                    htmlFor="phone"
                    style={{
                      display: 'block',
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '10px',
                      letterSpacing: '1.5px',
                      textTransform: 'uppercase',
                      color: '#42514c',
                      marginBottom: '8px',
                      fontWeight: 600
                    }}>
                    
                      Phone Number <span style={{ color: '#7b8c89', fontWeight: 400 }}>(Optional)</span>
                    </label>
                    <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleFormChange}
                    placeholder="(845) 555-0100"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '1px solid #dbdbdb',
                      borderRadius: '0px',
                      fontFamily: '"Gentium Basic", serif',
                      fontSize: '16px',
                      color: '#42514c',
                      backgroundColor: '#ffffff',
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => {
                      (e.currentTarget as HTMLInputElement).style.borderColor = '#5199a8';
                    }}
                    onBlur={(e) => {
                      (e.currentTarget as HTMLInputElement).style.borderColor = '#dbdbdb';
                    }} />
                  
                  </div>

                  {/* Message */}
                  <div className="mb-6">
                    <label
                    htmlFor="message"
                    style={{
                      display: 'block',
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '10px',
                      letterSpacing: '1.5px',
                      textTransform: 'uppercase',
                      color: '#42514c',
                      marginBottom: '8px',
                      fontWeight: 600
                    }}>
                    
                      Message <span style={{ color: '#5199a8' }}>*</span>
                    </label>
                    <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleFormChange}
                    placeholder="Please describe your service inquiry or how we can help you..."
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '1px solid #dbdbdb',
                      borderRadius: '0px',
                      fontFamily: '"Gentium Basic", serif',
                      fontSize: '16px',
                      color: '#42514c',
                      backgroundColor: '#ffffff',
                      outline: 'none',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      transition: 'border-color 0.2s',
                      lineHeight: '26px'
                    }}
                    onFocus={(e) => {
                      (e.currentTarget as HTMLTextAreaElement).style.borderColor = '#5199a8';
                    }}
                    onBlur={(e) => {
                      (e.currentTarget as HTMLTextAreaElement).style.borderColor = '#dbdbdb';
                    }} />
                  
                  </div>

                  <button
                  type="submit"
                  disabled={formStatus === 'sending'}
                  className="w-full transition-colors duration-200"
                  style={{
                    backgroundColor: formStatus === 'sending' ? '#7b8c89' : '#5199a8',
                    color: '#ffffff',
                    padding: '12px 28px',
                    fontFamily: 'Raleway, sans-serif',
                    letterSpacing: '2px',
                    fontSize: '12px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    border: 'none',
                    cursor: formStatus === 'sending' ? 'not-allowed' : 'pointer',
                    borderRadius: '0px'
                  }}
                  onMouseEnter={(e) => {
                    if (formStatus !== 'sending') {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#5cb0c2';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (formStatus !== 'sending') {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#5199a8';
                    }
                  }}>
                  
                    {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
                  </button>

                  <p
                  className="mt-3 text-center"
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '10px',
                    letterSpacing: '0.5px',
                    color: '#7b8c89'
                  }}>
                  
                    Your message will be sent to {contactInfo.email}
                  </p>
                </form>
              }
            </div>
          </div>
        </div>
      </section>

      {/* ─── Footer ─────────────────────────────────────────────────────── */}
      <footer
        style={{ backgroundColor: '#42514c', color: '#ffffff' }}>
        
        {/* Main Footer */}
        <div className="max-w-5xl mx-auto px-6 py-14">
          <div className="grid md:grid-cols-3 gap-10">
            {/* Brand */}
            <div>
              <div
                className="mb-4"
                style={{
                  fontFamily: 'Raleway, sans-serif',
                  fontSize: '20px',
                  letterSpacing: '2px',
                  color: '#ffffff',
                  fontWeight: 600
                }}>
                
                Dental Quest Laboratory, Inc.
              </div>
              <p
                style={{
                  fontFamily: '"Gentium Basic", serif',
                  fontSize: '15px',
                  lineHeight: '26px',
                  color: 'rgba(255,255,255,0.7)'
                }}>
                
                Full-service dental laboratory in Valley Cottage, NY. Over 40 years of expertise in restorative dentistry. FDA certified with 100% satisfaction guarantee.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <div
                className="mb-5"
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '10px',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  color: '#5199a8',
                  fontWeight: 600
                }}>
                
                Quick Links
              </div>
              <ul className="space-y-2">
                {navItems.map((item) =>
                <li key={item.href}>
                    <button
                    onClick={() => handleNavClick(item.href)}
                    className="transition-colors duration-200"
                    style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '11px',
                      letterSpacing: '0.5px',
                      color: 'rgba(255,255,255,0.7)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.color = '#5199a8';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.7)';
                    }}>
                    
                      {item.label}
                    </button>
                  </li>
                )}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <div
                className="mb-5"
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '10px',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  color: '#5199a8',
                  fontWeight: 600
                }}>
                
                Contact Information
              </div>
              <div className="space-y-3">
                <div
                  style={{
                    fontFamily: '"Gentium Basic", serif',
                    fontSize: '15px',
                    lineHeight: '24px',
                    color: 'rgba(255,255,255,0.7)'
                  }}>
                  
                  📍 {contactInfo.address}
                </div>
                <div>
                  <a
                    href={`tel:${contactInfo.phone}`}
                    style={{
                      fontFamily: '"Gentium Basic", serif',
                      fontSize: '15px',
                      color: 'rgba(255,255,255,0.7)',
                      textDecoration: 'none'
                    }}>
                    
                    📞 {contactInfo.phone}
                  </a>
                </div>
                <div>
                  <a
                    href={`mailto:${contactInfo.email}`}
                    style={{
                      fontFamily: '"Gentium Basic", serif',
                      fontSize: '14px',
                      color: 'rgba(255,255,255,0.7)',
                      textDecoration: 'none',
                      wordBreak: 'break-all'
                    }}>
                    
                    ✉️ {contactInfo.email}
                  </a>
                </div>
                <div
                  style={{
                    fontFamily: '"Gentium Basic", serif',
                    fontSize: '15px',
                    color: 'rgba(255,255,255,0.7)'
                  }}>
                  
                  🕐 {contactInfo.hours}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          className="border-t py-5"
          style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          
          <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3">
            <p
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '10px',
                letterSpacing: '0.5px',
                color: 'rgba(255,255,255,0.5)'
              }}>
              
              © {new Date().getFullYear()} Dental Quest Laboratory, Inc. All rights reserved.
            </p>
            <p
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '10px',
                letterSpacing: '0.5px',
                color: 'rgba(255,255,255,0.5)'
              }}>
              
              495 Kings Hwy, Valley Cottage, NY 10989 | FDA Certified Dental Laboratory
            </p>
          </div>
        </div>
      </footer>
    </div>);

}